/**
 * S-76: Resend エンドユーザー宛送信の構造的ガード（純関数 / Web SDK 非依存）。
 *
 * 契約（統合版19 別紙3 DPA 第5条2項 / 別添②）:
 *   エンドユーザー（来店客）への連絡は LINE 限定。米国法人 Resend には
 *   **エンドユーザーのメールアドレスを渡さない**（= 個情法28条の越境を発生させない）。
 *   Resend の宛先は「管理者連絡先」に限定する。
 *
 * このモジュールはその設計を **型 + 実行時の二層** で固定する:
 *   1. 型レベル: ブランド型 `AdminEmail`。Resend 送信ラッパの宛先を `AdminEmail[]` に縛り、
 *      生の `string`（= `customers.email` / `users.email` 等のエンドユーザー値）を
 *      そのまま渡すとコンパイルエラーになる。
 *   2. provenance allowlist: `AdminEmail` への唯一の入口 `toAdminEmail(email, source)` が
 *      **許可された管理者ソースの宣言**（`AdminEmailSource`）を要求する。`customers` /
 *      `users` といったエンドユーザー由来は宣言できない（型に存在しない）。
 *   3. 実行時: `assertAdminRecipients` を送信直前に呼び、空・不正形式・`as` キャスト等の
 *      抜け道を最終防衛線で弾く。
 *
 * 既存の S-78 / S-79 ガード（`shared/src/tenantContext.ts` の純 validator +
 * `functions/src/_shared/callableGuards.ts` の境界 assert）と同方針。
 */
/**
 * メールアドレスの最小形式チェック用。RFC 5322 の厳密実装ではなく、
 * 「空白なし・`@` を1つ含み・ローカル部とドメイン部が非空・ドメインに `.` を含む」
 * 程度の実用的バリデーション（Resend 側でも検証されるため過剰に厳格にしない）。
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * メールアドレスとして妥当な形式か（純関数 / **値をそのまま検証し trim しない**）。
 * trim は呼出側の責務（`toAdminEmail` が入力 trim 後にこれを呼ぶ）。これにより
 * `assertAdminRecipients` は前後空白付きの値（型迂回で混入）を正しく不正と判定できる。
 */
export function isValidEmailFormat(value) {
    return typeof value === 'string' && EMAIL_RE.test(value);
}
/**
 * 1件の管理者メールアドレスを検証して `AdminEmail` に変換する（唯一の入口）。
 *
 * @param email  管理者宛メールアドレス
 * @param source 由来（許可済み管理者ソースのみ宣言可能 = provenance allowlist）
 * @throws 形式不正な場合
 */
export function toAdminEmail(email, source) {
    const trimmed = typeof email === 'string' ? email.trim() : '';
    if (!isValidEmailFormat(trimmed)) {
        throw new Error(`toAdminEmail: invalid admin email format (source=${source})`);
    }
    return trimmed;
}
/**
 * 複数の管理者メールアドレスをまとめて `AdminEmail[]` に変換する。
 * 空配列も許容する（呼出側で「宛先なし=skip」を判断する設計のため）。
 *
 * @throws いずれかが形式不正な場合
 */
export function toAdminEmails(emails, source) {
    return emails.map((e) => toAdminEmail(e, source));
}
/**
 * Resend 送信直前に呼ぶ実行時アサーション（最終防衛線 / S-76）。
 *
 * 型レベルの `AdminEmail` ブランドを `as` キャストで迂回した場合や、JS 経路からの
 * 呼出に対し、送信境界で **空・非配列・不正形式** を弾く。形式チェックに通った
 * well-formed なアドレスを管理者宛と保証するのは型 + provenance 構造の責務
 * （DB 逆引きによる宛先実在検証は本ガードのスコープ外 / S-76 issue 参照）。
 *
 * @throws 宛先が空・不正形式を含む場合
 */
export function assertAdminRecipients(recipients) {
    if (!Array.isArray(recipients) || recipients.length === 0) {
        throw new Error('assertAdminRecipients: recipients must be a non-empty array');
    }
    for (const r of recipients) {
        if (!isValidEmailFormat(r)) {
            throw new Error(`assertAdminRecipients: invalid recipient address (${String(r)})`);
        }
    }
}
//# sourceMappingURL=adminEmail.js.map